import axios from 'axios';
import CryptoJS from 'crypto-js';

class FatSecretService {
  constructor() {
    this.clientId = '10aa4e5898ec441b91adfd0c1a2cf47c';
    this.clientSecret = '74436a1a20964d9c80bcc16945ffb962';
    this.baseUrl = 'https://platform.fatsecret.com/rest/server.api';
    this.accessToken = null;
    this.tokenExpiry = null;
  }

  async getAccessToken() {
    // Check if we have a valid token already
    if (this.accessToken && this.tokenExpiry && Date.now() < this.tokenExpiry) {
      return this.accessToken;
    }

    try {
      const response = await axios({
        method: 'post',
        url: 'https://oauth.fatsecret.com/connect/token',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        auth: {
          username: this.clientId,
          password: this.clientSecret,
        },
        data: 'grant_type=client_credentials&scope=basic',
      });

      this.accessToken = response.data.access_token;
      // Set expiry to slightly less than the actual expiry time to be safe
      this.tokenExpiry = Date.now() + (response.data.expires_in * 1000) - 60000;
      return this.accessToken;
    } catch (error) {
      console.error('Error getting FatSecret access token:', error);
      throw error;
    }
  }

  async searchFoods(query, maxResults = 50) {
    try {
      const token = await this.getAccessToken();
      
      const response = await axios({
        method: 'post',
        url: this.baseUrl,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Bearer ${token}`
        },
        data: new URLSearchParams({
          method: 'foods.search',
          search_expression: query,
          format: 'json',
          max_results: maxResults
        }).toString()
      });

      // Handle case where no foods are found
      if (!response.data.foods || !response.data.foods.food) {
        return [];
      }

      // Convert to array if single item is returned
      const foods = Array.isArray(response.data.foods.food) 
        ? response.data.foods.food 
        : [response.data.foods.food];

      return foods.map(food => this.formatFatSecretFood(food));
    } catch (error) {
      console.error('Error searching foods:', error);
      return [];
    }
  }

  async getFoodDetails(foodId) {
    try {
      const token = await this.getAccessToken();
      
      const response = await axios({
        method: 'post',
        url: this.baseUrl,
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          'Authorization': `Bearer ${token}`
        },
        data: new URLSearchParams({
          method: 'food.get.v2',
          food_id: foodId,
          format: 'json'
        }).toString()
      });

      if (!response.data.food) {
        throw new Error('Food details not found');
      }

      return this.formatFatSecretFoodDetails(response.data.food);
    } catch (error) {
      console.error('Error getting food details:', error);
      return null;
    }
  }

  formatFatSecretFood(food) {
    // Extract numeric values from string
    const getNumericValue = (str) => {
      if (!str) return 0;
      const match = str.match(/[\d.]+/);
      return match ? parseFloat(match[0]) : 0;
    };

    // Extract serving info from description
    let servingSize = 100;
    let servingUnit = 'g';

    if (food.food_description) {
      const servingMatch = food.food_description.match(/Per (\d+)(\w+)/i);
      if (servingMatch) {
        servingSize = parseInt(servingMatch[1], 10);
        servingUnit = servingMatch[2].toLowerCase();
      }
    }

    // Try to determine food category
    const category = this.categorizeFood(food.food_name);

    return {
      id: `fatsecret_${food.food_id}`,
      name: food.food_name,
      category: category,
      calories: getNumericValue(food.food_description?.match(/Calories: ([\d.]+)/)?.[1] || '0'),
      protein: getNumericValue(food.food_description?.match(/Protein: ([\d.]+)g/)?.[1] || '0'),
      carbs: getNumericValue(food.food_description?.match(/Carbs: ([\d.]+)g/)?.[1] || '0'),
      fat: getNumericValue(food.food_description?.match(/Fat: ([\d.]+)g/)?.[1] || '0'),
      servingSize: servingSize,
      servingUnit: servingUnit,
      servingOptions: ['g', 'oz', 'cup', servingUnit].filter((v, i, a) => a.indexOf(v) === i),
      source: 'FatSecret',
      isExternal: true,
      brandName: food.brand_name || null,
      foodType: food.food_type || null
    };
  }

  formatFatSecretFoodDetails(food) {
    // Get the default serving (usually 100g)
    let defaultServing = food.servings.serving;
    if (Array.isArray(defaultServing)) {
      defaultServing = defaultServing.find(s => s.serving_description === '100g') || defaultServing[0];
    }

    // Create serving options
    const servingOptions = Array.isArray(food.servings.serving)
      ? food.servings.serving.map(s => s.serving_description)
      : [food.servings.serving.serving_description];

    // Ensure 'g', 'oz', 'cup' are always options for conversion
    ['g', 'oz', 'cup'].forEach(unit => {
      if (!servingOptions.includes(unit)) servingOptions.push(unit);
    });

    // Extract serving unit from description
    const servingMatch = defaultServing.serving_description.match(/(\d+)(\w+)/);
    const servingUnit = servingMatch ? servingMatch[2].toLowerCase() : 'g';
    
    // Try to determine food category
    const category = this.categorizeFood(food.food_name);

    return {
      id: `fatsecret_${food.food_id}`,
      name: food.food_name,
      category: category,
      calories: parseFloat(defaultServing.calories || 0),
      protein: parseFloat(defaultServing.protein || 0),
      carbs: parseFloat(defaultServing.carbohydrate || 0),
      fat: parseFloat(defaultServing.fat || 0),
      servingSize: parseFloat(defaultServing.metric_serving_amount || 100),
      servingUnit: servingUnit,
      servingOptions: servingOptions.filter((v, i, a) => a.indexOf(v) === i),
      source: 'FatSecret',
      isExternal: true,
      brandName: food.brand_name || null,
      foodType: food.food_type || null,
      servingsDetails: Array.isArray(food.servings.serving) 
        ? food.servings.serving 
        : [food.servings.serving]
    };
  }

  categorizeFood(name) {
    const nameUpper = name.toUpperCase();
    
    // Protein sources
    if (nameUpper.includes('CHICKEN') || nameUpper.includes('BEEF') || 
        nameUpper.includes('PORK') || nameUpper.includes('FISH') || 
        nameUpper.includes('SALMON') || nameUpper.includes('TUNA') ||
        nameUpper.includes('EGG') || nameUpper.includes('PROTEIN') || 
        nameUpper.includes('MEAT') || nameUpper.includes('POULTRY') ||
        nameUpper.includes('TURKEY') || nameUpper.includes('YOGURT') ||
        nameUpper.includes('MILK') || nameUpper.includes('COTTAGE CHEESE')) {
      return 'Protein';
    }

    // Fat sources
    if (nameUpper.includes('OIL') || nameUpper.includes('BUTTER') || 
        nameUpper.includes('NUT') || nameUpper.includes('AVOCADO') || 
        nameUpper.includes('CHEESE') || nameUpper.includes('CREAM') ||
        nameUpper.includes('MAYO') || nameUpper.includes('OLIVE') ||
        nameUpper.includes('COCONUT')) {
      return 'Fat';
    }

    // Vegetables
    if (nameUpper.includes('BROCCOLI') || nameUpper.includes('SPINACH') || 
        nameUpper.includes('CARROT') || nameUpper.includes('LETTUCE') || 
        nameUpper.includes('TOMATO') || nameUpper.includes('PEPPER') ||
        nameUpper.includes('VEGETABLE') || nameUpper.includes('GREENS') ||
        nameUpper.includes('KALE') || nameUpper.includes('CELERY') ||
        nameUpper.includes('ONION') || nameUpper.includes('CUCUMBER')) {
      return 'Vegetable';
    }

    // Carbohydrates
    if (nameUpper.includes('RICE') || nameUpper.includes('BREAD') || 
        nameUpper.includes('PASTA') || nameUpper.includes('POTATO') || 
        nameUpper.includes('FRUIT') || nameUpper.includes('APPLE') ||
        nameUpper.includes('BANANA') || nameUpper.includes('GRAIN') ||
        nameUpper.includes('CEREAL') || nameUpper.includes('OAT') ||
        nameUpper.includes('WHEAT') || nameUpper.includes('CORN') ||
        nameUpper.includes('BEAN') || nameUpper.includes('SUGAR')) {
      return 'Carbohydrate';
    }

    // Default
    return 'Carbohydrate';
  }
}

export default new FatSecretService();